import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import config from '../../../config/Constant';
import { downloadFile, getRequest } from '../../../helpers/apiHandler';
import { hideCostForUser } from '../../../helpers/helperFunction';
import CustomAlert from '../../../components/CustomAlert';
import IconCalendar from '../../../assets/SidebarIcons/icon-calendar-time.svg';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import PieChart from '../../../components/Dashboard/SMSAnalytics/PieChart';

const CampaignAnalytics = () => {
  const { access_token, currency, email } = useSelector((state) => state.auth.user);
  const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';
  const hide_cost = hideCostForUser(email);

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [date, setDate] = useState([
    moment().subtract(1, 'month').startOf('day').toDate(),
    moment().toDate(),
  ]);
  const [smsData, setSmsData] = useState({ sent: 0, delivered: 0, in_progress: 0, failed: 0 });
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  const initialOverview = [
    { id: 'total_transactions', title: 'Total Transactions', value: 0 },
    { id: 'sent', title: 'Sent', value: 0 },
    { id: 'delivered', title: 'Delivered', value: 0, value_2: 0 },
    { id: 'in_progress', title: 'In progress', value: 0, value_2: 0 },
    { id: 'failed', title: 'Failed', value: 0, value_2: 0 },
  ];

  if (!white_label_perkeso && !hide_cost) {
    initialOverview.unshift({ id: 'total_cost', title: 'Total Cost', value: 0 });
  }

  const [overview, setOverview] = useState(initialOverview);
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const handleSmsAnalytics = () => {
    const data = {
      headers: { Authorization: access_token.token },
      params: {
        countries: selectedCountries,
        starts_at: date[0] ? moment(date[0]).format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null,
        ends_at: date[1] ? moment(date[1]).format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null
      },
    };

    getRequest('/v2/sms-analytics', data, (res) => {
      if (res.success) {
        setSmsData(res.data[0]);
        const { sent, delivered, in_progress, failed, total_cost, total_transactions } = res.data[0];

        setOverview((prevOverview) =>
          prevOverview.map((item) => {
            switch (item.id) {
              case 'total_cost':
                return { ...item, value: total_cost };
              case 'total_transactions':
                return { ...item, value: total_transactions };
              case 'sent':
                return { ...item, value: sent };
              case 'delivered':
                return { ...item, value: delivered, value_2: calculatePercent(delivered, sent) };
              case 'in_progress':
                return { ...item, value: in_progress, value_2: calculatePercent(in_progress, sent) };
              case 'failed':
                return { ...item, value: failed, value_2: calculatePercent(failed, sent) };
              default:
                return item;
            }
          }));
      }
    });
  };

  const handleDownload = () => {
    setLoadingDownload(true);

    downloadFile(
      {
        url: '/v2/sms/download-report',
        data: {
          starts_at: date[0] ? moment(date[0]).format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null,
          ends_at: date[1] ? moment(date[1]).format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null,
          countries: selectedCountries
        },
        access_token: access_token,
        fileName: 'sms-analytics.csv',
      },
      (res) => {
        setLoadingDownload(false);
        if (res.success) {
          setAlertState({
            message: 'Your sms analytics have been successfully exported to csv.',
            show: true,
            variant: 'success',
          });
        } else {
          setAlertState({
            message: 'We apologize, but it seems there was an issue exporting your analytics to csv.',
            show: true,
            variant: 'danger',
          });
        }
      }
    );
  };

  const getCountries = async () => {
    if (white_label_perkeso) return;

    const data = {
      headers: { Authorization: access_token.token },
      params: {},
    };

    getRequest('/v2/sms-analytics/countries', data, (res) => {
      if (res.success) {
        const options = res?.data.map(item => ({
          value: item.country,
          label: item.country
        }));

        setCountryOptions(options);
      }
    });
  };

  const onChangeCountry = (item) => {
    setSelectedCountry(item);

    if (item.length >= 1) {
      const country_iso2 = item.map(country => country.value);
      setSelectedCountries(country_iso2);
    } else {
      setSelectedCountries([]);
    }
  };

  const calculatePercent = (value, total, precision = 1) => {
    let result = 0;
    if (total > 0) result = (value / total) * 100;
    const symbol = '%';
    const multiplier = Math.pow(10, precision);

    return Math.round(result * multiplier) / multiplier + symbol;
  };

  useEffect(() => {
    handleSmsAnalytics();
  }, [date, selectedCountry]);

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: '#BDBDBD',
    }),
  };

  return (
    <div className="CampaignAnalytics">
      <div className="page-heading" style={{ padding: "24px 40px 32px" }}>
        <div className="container-left w-100">
          <div className="page-title">SMS Analytics</div>
        </div>
      </div>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <div className="bg-white" style={{ padding: '0 40px' }}>
        <div className="filter-campaign-analytics">
          {!white_label_perkeso && (
            <div className="react-select-wrapper">
              <Select
                styles={customStyles}
                placeholder="Select Country.."
                isMulti
                options={countryOptions}
                value={selectedCountry}
                onChange={onChangeCountry}
              />
            </div>
          )}
          <div>
            <DateRangePicker
              className="campaign-analytic-date-picker"
              value={date}
              onChange={setDate}
              clearIcon={null}
              calendarIcon={<img src={IconCalendar} alt="..." />}
              format="MM-dd-y"
              rangeDivider={<i className="fas fa-long-arrow-alt-right" />}
              dayPlaceholder="DD"
              monthPlaceholder="MM"
              yearPlaceholder="YYYY"
            />
          </div>
          {!hide_cost && (
            <div>
              <Button
                variant="primary"
                onClick={handleDownload}
                disabled={loadingDownload}
                style={{ minWidth: 160 }}
              >
                {loadingDownload && <Spinner animation="border" size="sm" className="mr-2" />}
                Download
              </Button>
            </div>
          )}
        </div>
        <Row className="mt-4">
          {overview.map((field) => {
            let color = '#344054';
            if (['failed'].includes(field.id)) color = '#F76659';
            if (['delivered'].includes(field.id)) color = '#38C669';
            if (['in_progress'].includes(field.id)) color = '#FFB347';
            return (
              <Col key={field.id} xs={12} md={12} lg={6} xl={4} style={{ marginBottom: 32 }}>
                <Card className="campaign-card">
                  <Card.Header>{field.title}</Card.Header>
                  <Card.Body>
                    <div className="KPIText" style={{ color }}>
                      {field.title === 'Total Cost' ? `${currency.symbol} ${field.value}` : field.value}
                    </div>
                    <div
                      className="KPISubText"
                      style={{
                        color: ['sent', 'total_cost', 'total_transactions'].includes(field.id)
                          ? 'transparent'
                          : '',
                      }}
                    >
                      {field.value_2}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
        <Row className="pb-5">
          <Col xs={12}>
            <div className="card" style={{ position: 'relative', height: '400px' }}>
              <PieChart title="Overall Delivery Rate" data={smsData} />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CampaignAnalytics;